import React, { forwardRef, Fragment, useEffect, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid, InputAdornment } from '@material-ui/core'
import Filters from 'components/FilterList/Filters'
import { Form } from 'react-final-form'
import Button from 'components/Button'
import Accordion from 'components/Accordion'
import { FieldText } from 'components/Fields'
import { useSelector, useDispatch } from 'react-redux'
import { getEcorregioesMarinho, getIsSavingEcorregioes, getIsUpdatingEcorregioes } from 'store/modules/ecorregioes/ecorregioes.selectors'
import { validateFormValues } from 'utils/forms'
import { useConfirmationDialog } from 'components/Dialog/ConfirmDialog'
import { callCriarEcorregiao, callAtualizarEcorregiao, callExcluirEcorregiao } from 'store/modules/ecorregioes'
import { getTipoUsuarioLogado } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'

const useStyles = makeStyles((theme) => ({
  rowText: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(5),
    fontSize: theme.typography.pxToRem(14),
  },
  search: {
    display: 'flex',
    justifyContent: 'space-between',
    width: (props) => props.fullWidth && '100%',
  },
  hidden: {
    display: 'none',
  },
  formAdd: {
    transition: '0.20s ease-out',
    maxHeight: 0,
    overflow: 'hidden',
  },
  openFormAdd: {
    marginTop: '-10px',
    paddingTop: 10,
    maxHeight: '1300px',
    transition: '0.25s ease-in',
  },
  normalTop: {
    marginTop: '16px',
  },
  alignRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '0 !important',
  },
  itemAlignRight: {
    maxWidth: '190px',
  },
  listItems: {
    marginTop: '32px',
    border: '1px solid #C9CEDB',
    boxShadow: '0px 8px 32px rgba(15, 52, 68, 0.08)',
    borderRadius: '8px',
    padding: '0px 16px',
  },
  itemTitle: {
    fontFamily: 'Poppins',
    color: '#0F3444',
    fontWeight: '600',
    fontSize: '16px',
  },
  divider: {
    height: 1,
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    backgroundColor: theme.palette.grey.main,
  },
  dividerField: {
    borderBottom: `1px solid ${theme.palette.grey.main}`,
    margin: '8px 0',
  },
  itemsForm: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    justifyContent: 'space-between',
    rowGap: '20px',
  },
  itemFormField: {
    width: 'calc( 50% - 4px )',
  },
  itemFormFull: {
    width: '100%',
  },
  inputAdornment: {
    marginRight: theme.spacing(2),
  },
}))

const TabMarinha = forwardRef(({ paisId, controlAdded, setControlAdded }, ref) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSaving = useSelector(getIsSavingEcorregioes)
  const isUpdating = useSelector(getIsUpdatingEcorregioes)
  const tipoUsuarioLogado = useSelector(getTipoUsuarioLogado)
  const filterScreen = [
    { value: true, label: 'filtros.nomeAZ', name: 'order' },
    { value: false, label: 'filtros.nomeZA', name: 'order' },
  ]
  const dialog = useConfirmationDialog()
  const listaEcorregioesMarinho = useSelector(getEcorregioesMarinho)
  const [data, setData] = useState(listaEcorregioesMarinho)

  const desabilitarFormulario = Boolean(tipoUsuarioLogado === TIPO_USUARIO.ADMIN.OBSERVADOR)

  const validation = yup.object({
    nome: yup.string().required(),
    hectareAreaOriginal: yup.number().required(),
    percentualAreaDegradada: yup.number().required(),
    link: yup.string().url(),
  })

  const handleUpdate = (values) => {
    const { id } = values
    const payload = ({ ...values, paisId })
    delete payload.id
    dispatch(callAtualizarEcorregiao({ id, ecorregiao: payload }))
  }

  const handleSave = (values, form) => {
    const payload = ({ ...values, paisId, marinho: true, terrestre: false })
    dispatch(callCriarEcorregiao(payload))
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const handleDelete = (id, ecorregiao) => dialog.showConfirmation({
    title: t('dialogs.confirmarExclusao'),
    message: t('dialogs.excluirEcorregiao', {
      ecorregiao,
    }),
  }).then((result) => {
    if (result) {
      dispatch(callExcluirEcorregiao({ id }))
    }
    return null
  })

  const [openItem, setOpenItem] = useState('')

  const handleExpand = (item) => {
    setOpenItem(openItem === item ? '' : item)
  }

  useImperativeHandle(
    ref,
    () => ({
      handleButtonAdded() {
        setControlAdded(true)
        handleExpand('')
      },
    }),
  )

  const clearFields = (form, values, type) => {
    if (type === 'add') {
      setControlAdded(!controlAdded)
    }
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const formFields = [
    {
      name: 'nome',
      label: t('inputs.Ecorregiao'),
      type: 'text',
      classe: classes.itemFormFull,
      required: true,
    },
    {
      name: 'link',
      label: t('inputs.LinkMapa'),
      type: 'text',
      classe: classes.itemFormFull,
      required: false,
    },
    {
      name: 'hectareAreaOriginal',
      label: t('inputs.AreaOriginal'),
      type: 'number',
      classe: classes.itemFormFull,
      required: true,
    },
    {
      name: 'hectareAreaRemanescente',
      label: t('inputs.AreaRemanescente'),
      type: 'number',
      classe: classes.itemFormField,
      required: false,
    },
    {
      name: 'percentualAreaRemanescente',
      label: t('inputs.PorcentagemRemanescente'),
      type: 'number',
      classe: classes.itemFormField,
      required: false,
      suffix: '%',
    },
    {
      name: 'hectareAreaDegradada',
      label: t('inputs.AreaDegradada'),
      type: 'number',
      classe: classes.itemFormField,
      required: false,
    },
    {
      name: 'percentualAreaDegradada',
      label: t('inputs.PorcentagemDegradada'),
      type: 'number',
      classe: classes.itemFormField,
      required: true,
      suffix: '%',
    },
  ]

  const FieldsForm = () => (
    <div className={classes.itemsForm}>
      {formFields.map((elem, index) => (
        <FieldText
          required={elem.required}
          label={elem.label}
          name={elem.name}
          key={index}
          className={elem.classe}
          type={elem.type}
          {...(elem.suffix ? {
            InputProps: {
              endAdornment: (
                <InputAdornment position="end" className={classes.inputAdornment}>
                  %
                </InputAdornment>
              ),
            },
          } : {})}
          disabled={desabilitarFormulario}
        />
      ))}
    </div>
  )

  const ItemForm = (position) => {
    const itemEcorregiao = [data[position]]
    return (
      <Form
        onSubmit={handleUpdate}
        validate={validateFormValues(validation)}
        initialValues={{
          nome: itemEcorregiao[0]?.nome ?? '',
          hectareAreaOriginal: itemEcorregiao[0]?.hectareAreaOriginal ?? '',
          hectareAreaRemanescente: itemEcorregiao[0]?.hectareAreaRemanescente ?? '',
          hectareAreaDegradada: itemEcorregiao[0]?.hectareAreaDegradada ?? '',
          percentualAreaRemanescente: itemEcorregiao[0]?.percentualAreaRemanescente ?? '',
          percentualAreaDegradada: itemEcorregiao[0]?.percentualAreaDegradada ?? '',
          link: itemEcorregiao[0]?.link ?? '',
          marinho: true,
          terrestre: false,
          id: itemEcorregiao[0]?.id ?? '',
        }}
        render={({ handleSubmit, form, pristine, valid }) => (
          <form onSubmit={handleSubmit}>
            <Accordion
              hideButton
              variant="no-borders"
              classes={{ title: classes.itemTitle }}
              title={itemEcorregiao[0]?.nome}
              onDelete={() => {
                handleDelete(
                  itemEcorregiao[0]?.id,
                  itemEcorregiao[0]?.nome,
                )
                handleExpand('')
              }}
              expanded={position === openItem}
              onExpand={() => handleExpand(position)}
              disableDelete={desabilitarFormulario}
            >
              <Grid container>
                { FieldsForm() }
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={() => { form.reset(); handleExpand('') }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={Boolean(pristine || !valid) || desabilitarFormulario}
                        loading={isUpdating}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Accordion>
            <div className={classes.divider} />
          </form>
        )}
      />
    )
  }

  useEffect(() => {
    setData(listaEcorregioesMarinho)
  }, [listaEcorregioesMarinho])

  return (
    <>
      <div className={clsx(classes.search, controlAdded && classes.hidden)}>
        <Filters
          fullWidth
          items={listaEcorregioesMarinho}
          setItems={setData}
          origin={{ vertical: 'top', horizontal: 'right' }}
          filterScreen={filterScreen}
        />
      </div>
      <div
        className={clsx(
          classes.formAdd,
          controlAdded && classes.openFormAdd,
        )}
      >
        <Form
          onSubmit={handleSave}
          initialValues={{
            nome: '',
            hectareAreaOriginal: '',
            hectareAreaRemanescente: '',
            hectareAreaDegradada: '',
            percentualAreaRemanescente: '',
            percentualAreaDegradada: '',
            link: '',
          }}
          validate={validateFormValues(validation)}
          render={({ handleSubmit, form, values, valid }) => (
            <form onSubmit={handleSubmit}>
              <Grid container>
                { FieldsForm() }
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={(e) => {
                          e.preventDefault()
                          clearFields(form, values, 'add')
                        }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={!valid || desabilitarFormulario}
                        loading={isSaving}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </div>
      <div className={classes.listItems}>
        {
          data.length > 0 &&
          data.map((elem, index) => (
            <Fragment key={index}>
              {ItemForm(index)}
            </Fragment>
          ))
        }
      </div>
    </>
  )
})

TabMarinha.propTypes = {
  paisId: PropTypes.number,
  controlAdded: PropTypes.bool,
  setControlAdded: PropTypes.func,
}

export default TabMarinha
